/**
 * @fileoverview Message normalization service for consistent message handling across the application
 * 
 * @purpose Centralizes message normalization logic to ensure consistent role handling, RAG extraction,
 * and agent debug metadata extraction across API routes and client components.
 * 
 * @usage Import and use MessageNormalizer.normalize() to normalize messages from database or API responses.
 * 
 * @ownership Portal SDK - shared message normalization
 * 
 * @dependencies 
 * - None (pure functions, no external dependencies)
 * 
 * @restrictions 
 * - DO NOT add side effects or async operations
 * - DO NOT modify input messages (always return new objects)
 * - DO NOT add database or API calls
 * 
 * @docs
 * - See plan: Message Persistence and Normalization Refactor
 */

export type ChatMessageRole = 'user' | 'assistant' | 'tool_execution' | 'summary_marker' | 'helper' | 'query' | 'system';

export type RAGResult = {
  content: string;
  title?: string;
  similarity?: number;
  sourceId?: string | number;
};

export type AgentDebug = Record<string, unknown>;

export type ToolCall = {
  id: string;
  name: string;
  arguments: Record<string, unknown>;
  result?: string;
  success?: boolean;
  error?: string;
};

/**
 * Raw message from database or API (before normalization)
 */
export type RawMessage = {
  id: string;
  threadId?: string;
  role: string;
  content?: string | null;
  contentSoFar?: string | null;
  status?: string;
  timestamp?: number;
  createdAt?: Date | string | number;
  updatedAt?: Date | string | number | null;
  toolCalls?: unknown[] | null;
  toolResults?: unknown[] | null;
  tokensIn?: number | null;
  tokensOut?: number | null;
  costCents?: number | null;
  processingTime?: number | null;
  isQueryMessage?: boolean;
  isHelperMessage?: boolean;
  ragResults?: RAGResult[];
  metadata?: Record<string, unknown>;
  files?: Array<{
    name: string;
    type: string;
    size: number;
  }>;
};

/**
 * Normalized message (after processing)
 */
export type NormalizedMessage = {
  id: string;
  role: ChatMessageRole;
  content: string;
  timestamp: number;
  toolCalls?: ToolCall[];
  toolResults?: unknown[];
  files?: Array<{
    name: string;
    type: string;
    size: number;
  }>;
  metadata?: Record<string, unknown>;
  // Extended fields
  tokensIn?: number;
  tokensOut?: number;
  costCents?: number;
  processingTime?: number;
  createdAt?: string;
  updatedAt?: string;
  isQueryMessage?: boolean;
  isHelperMessage?: boolean;
  ragResults?: RAGResult[];
};

export type NormalizeOptions = {
  /**
   * Whether to extract RAG results from toolCalls. Default: true
   */
  extractRagResults?: boolean;
  /**
   * Whether to extract agent debug metadata from toolCalls. Default: true
   */
  extractAgentDebug?: boolean;
  /**
   * Whether to normalize tool calls. Default: true
   */
  normalizeToolCalls?: boolean;
}

/**
 * Message normalization service
 * 
 * Provides consistent message normalization across the application, handling:
 * - Role normalization (user, assistant, tool_execution, summary_marker, query, helper)
 * - Content normalization (empty content, JSON parsing for legacy messages)
 * - RAG results extraction from toolCalls
 * - Agent debug metadata extraction
 * - Tool call normalization
 */
export class MessageNormalizer {
  /**
   * Normalize a single message role
   */
  static normalizeRole(role: string): ChatMessageRole {
    if (role === 'user') {
      return 'user';
    }
    if (role === 'assistant') {
      return 'assistant';
    }
    if (role === 'tool_execution' || role === 'tool') {
      return 'tool_execution';
    }
    if (role === 'summary_marker') {
      return 'summary_marker';
    }
    if (role === 'helper') {
      return 'helper';
    }
    if (role === 'query') {
      return 'query';
    }
    if (role === 'system') {
      return 'system';
    }
    // Default to assistant for unknown roles
    return 'assistant';
  }

  /**
   * Extract RAG results from toolCalls
   */
  static extractRagResults(toolCalls: unknown[] | null | undefined): RAGResult[] | undefined {
    if (!toolCalls || !Array.isArray(toolCalls)) {
      return undefined;
    }

    for (const tc of toolCalls) {
      if (typeof tc === 'object' && tc !== null) {
        const tcObj = tc as Record<string, unknown>;
        if (tcObj.kind === 'rag_results' && Array.isArray(tcObj.ragResults)) {
          return tcObj.ragResults as RAGResult[];
        }
      }
    }

    return undefined;
  }

  /**
   * Extract agent debug metadata from toolCalls
   */
  static extractAgentDebug(toolCalls: unknown[] | null | undefined): AgentDebug | undefined {
    if (!toolCalls || !Array.isArray(toolCalls)) {
      return undefined;
    }

    for (const tc of toolCalls) {
      if (typeof tc === 'object' && tc !== null) {
        const tcObj = tc as Record<string, unknown>;
        if (tcObj.kind === 'agent_debug') {
          return tcObj as AgentDebug;
        }
      }
    }

    return undefined;
  }

  /**
   * Normalize tool call structure
   */
  static normalizeToolCall(
    tc: unknown,
    index: number,
    role: ChatMessageRole
  ): ToolCall | null {
    if (typeof tc !== 'object' || tc === null) {
      return null;
    }

    const tcObj = tc as Record<string, unknown>;

    // For summary_marker messages, preserve metadata as-is (don't normalize)
    if (role === 'summary_marker') {
      return null; // Summary markers don't have normalized tool calls
    }

    // For query/helper messages, skip RAG results and agent debug (already extracted)
    if ((role === 'query' || role === 'helper') && 
        (tcObj.kind === 'rag_results' || tcObj.kind === 'agent_debug')) {
      return null;
    }

    // Extract name from various possible structures
    const name = 
      (tcObj.name as string | undefined) ||
      ((tcObj.function as Record<string, unknown> | undefined)?.name as string | undefined) ||
      (tcObj.toolName as string | undefined) ||
      'tool';

    // Extract arguments from various possible structures (may be string or object)
    const args: string | Record<string, unknown> | undefined =
      (tcObj.arguments as string | Record<string, unknown> | undefined) ??
      ((tcObj.function as Record<string, unknown> | undefined)?.arguments as string | Record<string, unknown> | undefined) ??
      (tcObj.args as string | Record<string, unknown> | undefined);

    // Normalize arguments: if it's a string, try to parse to get the object
    let argsObject: Record<string, unknown>;
    if (typeof args === 'string' && args.trim() !== '') {
      try {
        argsObject = JSON.parse(args);
      } catch {
        argsObject = {};
      }
    } else if (args && typeof args === 'object' && !Array.isArray(args)) {
      argsObject = args as Record<string, unknown>;
    } else {
      argsObject = {};
    }

    // Normalize result: if it's a string, try to parse and re-stringify to fix double-encoding
    let resultString: string | undefined;
    if (tcObj.result === null || tcObj.result === '') {
      resultString = undefined;
    } else if (typeof tcObj.result === 'string') {
      try {
        const parsed = JSON.parse(tcObj.result);
        resultString = JSON.stringify(parsed);
      } catch {
        resultString = tcObj.result;
      }
    } else if (tcObj.result !== undefined) {
      resultString = JSON.stringify(tcObj.result);
    }

    // Preserve success field
    const successValue = tcObj.success !== undefined ? (tcObj.success as boolean) : undefined;

    return {
      id: (tcObj.id as string | undefined) ||
          (tcObj.callId as string | undefined) ||
          `call-${index}`,
      name,
      arguments: argsObject,
      result: resultString,
      success: successValue,
      error: tcObj.error as string | undefined,
    };
  }

  /**
   * Normalize message content (handle empty content, JSON parsing for legacy messages)
   */
  static normalizeContent(
    content: string | null | undefined,
    contentSoFar: string | null | undefined,
    role: ChatMessageRole
  ): string {
    // Prioritize content, fallback to contentSoFar, but never return null
    let normalizedContent = (content ? content : (contentSoFar ? contentSoFar : '')).trim();

    // Fix content if it's JSON (legacy messages that were saved with JSON content)
    if (role === 'tool_execution' && normalizedContent.trim().startsWith('{') && normalizedContent.includes('toolCall')) {
      try {
        const parsed = JSON.parse(normalizedContent);
        const toolCall = (parsed.toolCall || parsed) as Record<string, unknown>;
        const toolName = (toolCall.name as string | undefined) || 'tool';
        normalizedContent = `Executing tool: ${toolName}`;
      } catch {
        // If parsing fails, keep original content
      }
    }

    return normalizedContent;
  }

  /**
   * Normalize timestamp from various formats
   */
  static normalizeTimestamp(
    timestamp: number | undefined,
    createdAt: Date | string | number | undefined
  ): number {
    if (timestamp !== undefined) {
      return timestamp;
    }

    if (createdAt === undefined) {
      return Date.now();
    }

    if (typeof createdAt === 'number') {
      return createdAt;
    }

    if (typeof createdAt === 'string') {
      return new Date(createdAt).getTime();
    }

    if (createdAt instanceof Date) {
      return createdAt.getTime();
    }

    return Date.now();
  }

  /**
   * Normalize a single message
   */
  static normalizeMessage(
    msg: RawMessage,
    index: number,
    allMessages: RawMessage[],
    options: NormalizeOptions = {}
  ): NormalizedMessage {
    const {
      extractRagResults: shouldExtractRag = true,
      extractAgentDebug: shouldExtractDebug = true,
      normalizeToolCalls: shouldNormalizeToolCalls = true,
    } = options;

    // Normalize role
    let role = this.normalizeRole(msg.role);

    // If message has toolCalls but isn't marked as tool_execution, check if it's a tool message
    if (msg.toolCalls && Array.isArray(msg.toolCalls) && msg.toolCalls.length > 0 && role !== 'tool_execution') {
      const hasToolIndicators = 
        msg.content?.includes('Executing tool:') ||
        msg.content?.includes('Tool ') ||
        msg.toolCalls.some((tc: unknown) => {
          if (typeof tc === 'object' && tc !== null) {
            const tcObj = tc as Record<string, unknown>;
            return tcObj.name || (tcObj.function as Record<string, unknown> | undefined)?.name;
          }
          return false;
        });

      if (hasToolIndicators) {
        role = 'tool_execution';
      }
    }

    // Normalize content
    const content = this.normalizeContent(msg.content, msg.contentSoFar, role);

    // Normalize timestamp
    const timestamp = this.normalizeTimestamp(
      msg.timestamp,
      msg.createdAt instanceof Date ? msg.createdAt : msg.createdAt
    );

    // Extract RAG results and agent debug info from toolCalls
    let ragResults: RAGResult[] | undefined = msg.ragResults;
    let agentDebug: AgentDebug | undefined = msg.metadata?.agentDebug as AgentDebug | undefined;

    if ((role === 'query' || role === 'helper') && msg.toolCalls) {
      if (shouldExtractRag && !ragResults) {
        ragResults = this.extractRagResults(msg.toolCalls);
      }
      if (shouldExtractDebug && !agentDebug) {
        agentDebug = this.extractAgentDebug(msg.toolCalls);
      }
    }

    // Normalize tool calls
    let normalizedToolCalls: ToolCall[] | undefined;
    // summary_marker stores metadata (summarizedMessageCount, etc.) in toolCalls[0] - preserve as-is
    if (role === 'summary_marker' && msg.toolCalls && Array.isArray(msg.toolCalls) && msg.toolCalls.length > 0) {
      normalizedToolCalls = msg.toolCalls as ToolCall[];
    } else if (shouldNormalizeToolCalls && msg.toolCalls && Array.isArray(msg.toolCalls)) {
      normalizedToolCalls = msg.toolCalls
        .map((tc, tcIndex) => this.normalizeToolCall(tc, tcIndex, role))
        .filter((tc): tc is ToolCall => tc !== null);
      
      if (normalizedToolCalls.length === 0) {
        normalizedToolCalls = undefined;
      }
    } else if (msg.toolCalls && Array.isArray(msg.toolCalls) && role === 'tool_execution') {
      // For tool_execution, always normalize tool calls even if option is false
      normalizedToolCalls = msg.toolCalls
        .map((tc, tcIndex) => this.normalizeToolCall(tc, tcIndex, role))
        .filter((tc): tc is ToolCall => tc !== null);
      
      if (normalizedToolCalls.length === 0) {
        normalizedToolCalls = undefined;
      }
    }

    // Build normalized message
    const normalized: NormalizedMessage = {
      id: msg.id,
      role,
      content,
      timestamp,
      toolCalls: normalizedToolCalls,
      toolResults: msg.toolResults ? (msg.toolResults as unknown[]) : undefined,
      files: msg.files,
      tokensIn: msg.tokensIn ?? undefined,
      tokensOut: msg.tokensOut ?? undefined,
      costCents: msg.costCents ?? undefined,
      processingTime: msg.processingTime ?? undefined,
      createdAt: msg.createdAt instanceof Date 
        ? msg.createdAt.toISOString() 
        : typeof msg.createdAt === 'string' 
          ? msg.createdAt 
          : msg.createdAt 
            ? new Date(msg.createdAt).toISOString() 
            : undefined,
      updatedAt: msg.updatedAt 
        ? (msg.updatedAt instanceof Date 
            ? msg.updatedAt.toISOString() 
            : typeof msg.updatedAt === 'string' 
              ? msg.updatedAt 
              : new Date(msg.updatedAt).toISOString())
        : undefined,
    };

    // Add agent message flags and metadata (always extracted)
    if (role === 'query') {
      normalized.isQueryMessage = true;
      if (ragResults) {
        normalized.ragResults = ragResults;
      }
      if (agentDebug) {
        normalized.metadata = { ...normalized.metadata, agentDebug };
      }
    } else if (role === 'helper') {
      normalized.isHelperMessage = true;
      if (agentDebug) {
        normalized.metadata = { ...normalized.metadata, agentDebug };
      }
    }

    // Preserve existing metadata if present
    if (msg.metadata) {
      normalized.metadata = { ...normalized.metadata, ...msg.metadata };
    }

    // Preserve isQueryMessage/isHelperMessage flags if already set
    if (msg.isQueryMessage) {
      normalized.isQueryMessage = true;
    }
    if (msg.isHelperMessage) {
      normalized.isHelperMessage = true;
    }

    return normalized;
  }

  /**
   * Normalize an array of messages
   */
  static normalize(
    messages: RawMessage[],
    options: NormalizeOptions = {}
  ): NormalizedMessage[] {
    return messages.map((msg, index) => 
      this.normalizeMessage(msg, index, messages, options)
    );
  }
}

